// CAS per-symbol trading session — the single source of truth for `session` and
// `corrections` on a chart symbol.
//
// WHY THIS EXISTS
// `session` has always come from the per-SEGMENT blob (aux/symbol_segments) or
// from aux/symbol. Neither can express what CAS needs from 2026-08-03:
// F&O-eligible cash equities stop continuous trading at 15:15 while everything
// else in the same segment keeps 15:30. `data/session_history` answers it per
// symbol and is therefore PRIMARY; aux/symbol's own session is the fallback.
//
// Getting it wrong is not cosmetic. Under the segment default 0915-1530 the
// 15:15-15:30 auction counts as IN session, so auction prints pass the tick
// guard and can write the daily/weekly close — the exact bug CAS creates.
//
// WHY IT IS A SEPARATE MODULE
// It used to live inside UDFCompatibleDatafeedBase, which drags in the socket
// stream, OTel, the custom studies and the whole TV model layer — untestable
// without a browser. Everything here is pure logic over one injected `post`
// function and one injected clock, so test/session-source.test.mjs drives the
// real code paths directly.
//
// Contract: never hand TradingView a session we have not validated. A malformed
// session string does not throw — it hangs the chart on a permanent spinner
// inside a web worker, and TV's own validators are dead code in v32.

// @ts-ignore -- plain-JS vendored module, no .d.ts (allowJs is on)
import { validateSessionString, mergeCorrections } from './vendor/session-timeline';

// What validateSessionString returns. Declared here because the module is plain
// JS: TypeScript infers a union whose `ok` is a plain boolean, so `!v.ok` does
// not narrow to the failure shape and reading `v.code` off it will not compile.
type SessionValidation =
    | { ok: true; entries: unknown[] }
    | { ok: false; code: string; detail?: string };

export type SessionEntry = { session: string; corrections: string };

// The subset of inhouse_requester's RequestMeta this module needs. `status` is 0
// when no response was received at all — a network error, a failed CORS
// preflight, or our own abort.
export interface SessionHttpMeta {
    status: number;
    ok: boolean;
    aborted: boolean;
    body: any;
    error?: string;
}

export type SessionPost = (
    body: { token: number; market_segment_id: number },
    signal?: AbortSignal
) => Promise<SessionHttpMeta>;

// Anything with a `session` and `corrections` we can override — kept structural
// so this module never imports the TV model layer.
export interface SessionCarrier {
    session?: string;
    corrections?: string;
}

// Outcome of one HTTP attempt. `retryable: true` means we never got a definitive
// answer and MUST NOT cache; false means the server answered and `entry` is that
// answer, even when it is null.
type Attempt = {
    retryable: boolean;
    transport: boolean;   // no HTTP response at all (network / CORS / abort)
    entry: SessionEntry | null;
    reason: string;
    code?: string;
    session?: string;
    error?: string;
};

export interface SessionSourceOptions {
    post: SessionPost;
    // QA hook: ?cas_session=...&cas_corrections=... forces a session so the
    // post-CAS regime can be exercised before it goes live, without touching the
    // device clock or the backend. Validated like any other, so it cannot hang
    // the chart. Never set in production.
    override?: SessionEntry | null;
    log?: (message: string) => void;
    // Injected so tests can drive time deterministically instead of sleeping.
    now?: () => number;
    setTimer?: (fn: () => void, ms: number) => any;
    clearTimer?: (handle: any) => void;
}

// Only cash equities change under CAS: NSE_EQ=1, BSE_EQ=3. F&O's 15:40 close is
// segment-uniform and rides the backend's own session instead. Indices are NOT
// excluded — they live in these segments too, and session_history is the
// per-symbol source of truth, so an index that keeps 15:30 is told so rather
// than guessed at.
export function isCasSegment(segment: unknown): boolean {
    const n = Number(segment);
    return n === 1 || n === 3;
}

export class SessionSource {
    // How long resolveSymbol may wait for a session before handing TV the
    // fallback. Nothing is painted until resolveSymbol calls back, so this is not
    // stolen from a rendered chart — it is one extra round trip, and only on the
    // first resolve of each cash-equity symbol (everything after is cached).
    // A browser round trip to this endpoint measures ~350ms, so 2000 is roughly
    // 5x margin; the circuit breaker, not this number, handles a dead endpoint.
    static readonly WAIT_MS = 2500;
    // How long the REQUEST may live, as opposed to how long we make TV wait. Much
    // larger on purpose: a reply arriving at 3s is useless for that paint but
    // still caches the right session for the next resolve, so aborting at the
    // wait would discard a correct answer. This abort exists to kill true black
    // holes — a hung socket never settles on its own, so without it the in-flight
    // entry leaks for the life of the page.
    static readonly FETCH_TIMEOUT_MS = 10000;
    static readonly RETRY_DELAY_MS = 400;
    // Per-symbol backoff after a transient failure. Nothing is cached (so the
    // symbol WILL be asked again and can recover), just not immediately.
    static readonly COOLDOWN_MS = 30000;
    // Endpoint-level circuit breaker. See _noteTransportFailure.
    static readonly BREAKER_TRIP_AFTER = 2;
    static readonly BREAKER_MS = 300000;
    // The gateway allows 5 requests/second per user per API on this endpoint, and a
    // 429 means that symbol gets NO session at all — so the limit has to be
    // respected as a RATE, not just as a concurrency cap. Four in flight at ~350ms
    // each would sustain ~11/s and trip it anyway.
    //
    // 4/s leaves headroom under the server's 5: the limit is per USER, so a second
    // tab or another device on the same login spends from the same budget and we
    // cannot see that traffic. A full 8-chart layout is the worst realistic case —
    // 8 symbols, so two one-second windows, all resolved well inside WAIT_MS.
    static readonly MAX_PER_SECOND = 4;
    // Still cap sockets: the rate limiter governs starts, this governs how many can
    // be outstanding at once when the endpoint is slow.
    static readonly MAX_CONCURRENT = 4;
    // A 429 is the gateway telling us to slow down. Pausing only the symbol that
    // was rejected is the wrong response — the pressure is on the endpoint, not on
    // that scrip — so hold the whole queue briefly instead.
    static readonly RATE_LIMIT_PAUSE_MS = 2000;
    // A queued lookup is useless once the resolve that wanted it has given up.
    // This matters at scale: a 100-symbol watchlist paced at 4/s would otherwise
    // spend 25 seconds firing requests whose answers nobody can apply any more,
    // against an endpoint that rate-limits at 5/s. Anything still queued past the
    // wait is dropped instead of sent. Priority lookups — the chart's own symbols —
    // are never dropped.
    static readonly QUEUE_MAX_AGE_MS = SessionSource.WAIT_MS;

    private readonly _post: SessionPost;
    private readonly _override: SessionEntry | null;
    private readonly _log: (message: string) => void;
    private readonly _now: () => number;
    private readonly _setTimer: (fn: () => void, ms: number) => any;
    private readonly _clearTimer: (handle: any) => void;

    // Definitive answers only: a valid session, a malformed one, or an
    // authoritative "no override for this symbol" (the normal reply for every
    // non-F&O scrip). Transport failures, 429s and 5xx are never stored here —
    // one blip must not be able to poison a symbol for the rest of the page.
    private _cache: Record<string, SessionEntry | null> = {};
    private _inFlight: Record<string, Promise<SessionEntry | null> | undefined> = {};
    private _cooldownUntil: Record<string, number> = {};
    // Sessions handed to us by a payload we were fetching anyway — today that is
    // aux/symbolsToTokens, the ONE bulk call the watchlist already makes for all of
    // its symbols at once. A seed answers a lookup without any request of its own,
    // which is the only way a long watchlist can cost nothing: TradingView resolves
    // every row and does NOT re-resolve when you later chart one (confirmed
    // against the running terminal), so we cannot simply decline to answer.
    //
    // Kept apart from _cache so the CHART's own symbol still goes to
    // data/session_history, which remains the primary source. A watchlist row uses
    // the seed; the symbol being drawn asks the authority.
    private _seeded: Record<string, SessionEntry | null> = {};

    // Endpoint-level breaker. A failed CORS preflight is indistinguishable from a
    // network error in JS (both surface as a rejected fetch with no status), and
    // it is DETERMINISTIC: when the route is not deployed on this environment's
    // gateway, every symbol would otherwise pay two requests and a full wait,
    // forever. Two transport failures are enough evidence to stop asking.
    private _transportFailures = 0;
    private _breakerUntil = 0;
    private _breakerLogged = false;

    // Outbound queue. Chart symbols are prewarmed and jump it, so the symbol the
    // user is looking at is never stuck behind a row of order-book lookups.
    private _active = 0;
    private _queue: Array<{ run: () => void; at: number; priority: boolean }> = [];
    private _droppedFromQueue = 0;
    private _dropLogged = false;
    private _pausedUntil = 0;
    private _drainTimer: any = null;
    private _rateLimitLogged = false;
    // Start times of recent requests, trimmed to a one-second rolling window.
    private _recentStarts: number[] = [];

    // What we last handed TV, purely so a session that lands after the bounded
    // wait can be reported. See noteHandedToTv for why it cannot be repaired.
    private _handedToTv: Record<string, { symbol: string; session: string }> = {};
    private _lateLogged: Record<string, boolean> = {};

    constructor(options: SessionSourceOptions) {
        this._post = options.post;
        this._override = options.override || null;
        this._log = options.log || function () { /* silent by default */ };
        this._now = options.now || (() => Date.now());
        this._setTimer = options.setTimer || ((fn, ms) => setTimeout(fn, ms));
        this._clearTimer = options.clearTimer || ((h) => clearTimeout(h));
    }

    // ----- fetching ---------------------------------------------------------

    key(segment: number | string, token: string | number): string {
        return `${Number(segment)}/${token}`;
    }

    breakerOpen(): boolean {
        return this._breakerUntil > this._now();
    }

    // Resolve the session for one scrip. Never rejects. Resolves null for "no
    // override", "not applicable" and "could not find out" alike — every caller
    // treats those identically (keep whatever session the symbol already had).
    // Accept a session that arrived inside another response. Validated like any
    // other — an unvalidated string reaching TradingView hangs the chart on a
    // permanent spinner. Returns whether it was usable.
    seed(segment: number | string, token: string | number, session?: string, corrections?: string): boolean {
        try {
            if (!isCasSegment(segment) || token == null || token === '') return false;
            if (!session || typeof session !== 'string') return false;
            const v = validateSessionString(session) as SessionValidation;
            if (!v.ok) {
                this._log(`SessionSource: ignoring a malformed seeded session for ${segment}/${token} (${v.code})`);
                return false;
            }
            this._seeded[this.key(segment, token)] = { session, corrections: String(corrections || '') };
            return true;
        } catch (e) {
            return false;
        }
    }

    // `forceNetwork` skips a seeded answer. Used for the chart's own symbols, so
    // data/session_history stays the primary source for what is actually drawn.
    get(segment: number | string, token: string | number, priority = false, forceNetwork = false): Promise<SessionEntry | null> {
        const seg = Number(segment);
        if (!isCasSegment(seg)) return Promise.resolve(null);
        if (token == null || token === '' || String(token) === '0') return Promise.resolve(null);

        const key = this.key(seg, token);
        if (Object.prototype.hasOwnProperty.call(this._cache, key)) {
            return Promise.resolve(this._cache[key]);
        }
        const inFlight = this._inFlight[key];
        if (inFlight) return inFlight;

        // The QA override answers BEFORE the network, not inside the response
        // handler. Otherwise an unreachable endpoint takes the failure path and
        // the override — the only way to exercise CAS before go-live — is
        // silently unusable exactly when it is most needed.
        if (this._override && this._override.session) {
            const ov = validateSessionString(this._override.session) as SessionValidation;
            const entry = ov.ok ? this._override : null;
            this._cache[key] = entry;
            return Promise.resolve(entry);
        }

        // A seed answers without a request. Deliberately after the in-flight check,
        // so a chart symbol whose network lookup is already running joins that
        // instead of taking the seed.
        if (!forceNetwork && Object.prototype.hasOwnProperty.call(this._seeded, key)) {
            return Promise.resolve(this._seeded[key]);
        }
        if (this.breakerOpen()) return Promise.resolve(null);
        if ((this._cooldownUntil[key] || 0) > this._now()) return Promise.resolve(null);

        const p = this._attempt(seg, token, priority)
            .then((res) => {
                // Retry ONLY what a retry can plausibly fix. A 429 or a 5xx is a
                // momentary server state; a transport failure is not — a blocked
                // CORS preflight or a missing route fails identically 400ms later,
                // and retrying it just doubles the noise in the one case we most
                // want to be quiet in. Nothing is cached either way, so a genuine
                // network blip is simply re-asked on the next resolve.
                if (!res.retryable || res.transport) return res;
                return this._sleep(SessionSource.RETRY_DELAY_MS).then(() => this._attempt(seg, token, priority));
            })
            .then((res) => {
                if (res.transport) this._noteTransportFailure(res.reason, res.error);
                else this._noteTransportSuccess();

                if (res.retryable) {
                    // Not a definitive answer: DO NOT cache. Leaving the key absent
                    // is what lets a later resolve ask again and recover; the
                    // cooldown stops that from becoming a hot loop.
                    this._cooldownUntil[key] = this._now() + SessionSource.COOLDOWN_MS;
                    this._log(`SessionSource: transient failure for ${key} (${res.reason}${res.error ? ': ' + res.error : ''}) — not cached`);
                    return null;
                }

                this._cache[key] = res.entry;
                if (res.reason === 'invalid') {
                    this._log(`SessionSource: rejected a malformed session for ${key}: ${res.session} (${res.code})`);
                } else if (res.reason !== 'ok' && res.reason !== 'no_session') {
                    this._log(`SessionSource: ${key} answered but unusable (${res.reason})`);
                }
                // If TV already resolved this symbol with the fallback session,
                // this is the moment we learn the answer was different.
                if (res.entry && res.entry.session) this._notifyLate(key, res.entry.session);
                return res.entry;
            })
            .catch((e: any) => {
                // Belt and braces: nothing above should throw, but a session
                // lookup must never be able to take the chart down with it.
                this._cooldownUntil[key] = this._now() + SessionSource.COOLDOWN_MS;
                this._log(`SessionSource: unexpected failure for ${key}: ${(e && e.message) || String(e)}`);
                return null;
            });

        // Clear the in-flight slot as part of the chain the callers await, so a
        // caller can never observe a settled promise still parked in the map.
        const tracked = p.then(
            (v) => { delete this._inFlight[key]; return v; },
            (e) => { delete this._inFlight[key]; throw e; }
        );
        this._inFlight[key] = tracked;
        return tracked;
    }

    // Start the lookup without waiting on it, so the bounded wait in resolveSymbol
    // is normally already satisfied by the time TV asks. Fire-and-forget by
    // design: callers are latency optimisations, not consumers.
    prewarm(segment: number | string, token: string | number): void {
        try {
            // forceNetwork: the chart's symbol asks the primary source even when a
            // watchlist payload already seeded one for it.
            const p = this.get(segment, token, true, true);
            if (p && typeof p.catch === 'function') p.catch(() => { /* best-effort */ });
        } catch (e) { /* best-effort prime */ }
    }

    // Resolve within a bounded wait. Returns null if the answer is not in hand in
    // time — the caller keeps the fallback session and the late re-resolve repairs
    // it. When the breaker is open there is nothing to wait FOR, so the wait is
    // skipped entirely rather than stalling every symbol switch by WAIT_MS.
    waitFor(segment: number | string, token: string | number): Promise<SessionEntry | null> {
        if (!isCasSegment(segment)) return Promise.resolve(null);
        if (this.breakerOpen()) return Promise.resolve(null);
        const key = this.key(segment, token);
        if (Object.prototype.hasOwnProperty.call(this._cache, key)) {
            return Promise.resolve(this._cache[key]);
        }
        const timeout = new Promise<null>((resolve) => {
            this._setTimer(() => resolve(null), SessionSource.WAIT_MS);
        });
        return Promise.race([this.get(segment, token), timeout]).catch(() => null);
    }

    // ----- applying ---------------------------------------------------------

    // Return a COPY of `info` carrying the resolved session. Never mutates the
    // input: it is the object held in the datafeed's symbolInfo cache, and writing
    // the session into it would make a cold-cache first resolve sticky forever.
    applyTo<T extends SessionCarrier>(info: T, entry: SessionEntry | null): T {
        if (!entry || !entry.session || entry.session === info.session) return info;
        const out: T = { ...(info as any) };
        out.session = entry.session;
        // Corrections are MERGED, not replaced. The symbol's own carry muhurat
        // sessions, half-days and exchange one-offs; session_history's carry the
        // per-date CAS pins — which is how the backend expresses a session change
        // starting MID-WEEK, since TV only begins a new session-history entry at a
        // week boundary. Dropping either loses real trading days.
        if (entry.corrections) {
            out.corrections = mergeCorrections(info.corrections, entry.corrections);
        }
        return out;
    }

    // Record what TV was actually handed.
    //
    // A session that lands AFTER the bounded wait cannot be applied to the chart
    // that is already showing. TV memoises symbolInfo at resolve time, and every
    // public route back into a resolve early-returns when the symbol is unchanged
    // — verified in the v32 bundle, both on the chart API
    //   setSymbol(e,t){ ... if(e===this.symbol()||o.symbolSameAsResolved(e)) return i?.(),!0; ...}
    // and on the widget-level _setSymbolImpl. resetData() re-requests BARS, not
    // symbolInfo. So an earlier version of this code that re-set the symbol was
    // silently doing nothing.
    //
    // That is why the real fix is upstream: register the chart's symbol BEFORE TV
    // resolves it (see the datafeed's prewarmSession) so the wait is entered with
    // a head start and effectively always wins. When it does lose, it keeps its
    // aux/symbol session — the documented secondary source — and the next visit to
    // that symbol is served from cache and correct. This method exists to make
    // that case visible rather than silent.
    noteHandedToTv(segment: number | string, token: string | number, symbol: string, session: string): void {
        this._handedToTv[this.key(segment, token)] = { symbol, session: String(session || '') };
    }

    private _notifyLate(key: string, session: string): void {
        try {
            if (!session || this._lateLogged[key]) return;
            const handed = this._handedToTv[key];
            if (!handed || !handed.symbol) return;   // TV never resolved it yet
            if (handed.session === session) return;  // the wait was won; nothing to do
            this._lateLogged[key] = true;
            this._log(
                `SessionSource: session for ${handed.symbol} arrived after the ${SessionSource.WAIT_MS}ms wait ` +
                `(${handed.session} -> ${session}). The chart keeps its aux/symbol session until the symbol is ` +
                `next resolved; TV cannot re-read symbolInfo in place.`
            );
        } catch (e) { /* diagnostics must never break the chart */ }
    }

    // ----- internals --------------------------------------------------------

    // Run `task` when a slot frees up and the queue is not paused. Resolves with
    // whatever the task resolves; the task itself never rejects.
    private _runQueued<T>(task: () => Promise<T>, priority: boolean): Promise<T> {
        return new Promise<T>((resolve) => {
            const start = () => {
                this._active += 1;
                task().then(
                    (v) => { this._active -= 1; this._drain(); resolve(v); },
                    (e) => { this._active -= 1; this._drain(); throw e; }
                );
            };
            // A dropped task resolves as a transport-shaped miss: nothing is cached,
            // so the symbol is simply asked again next time it is resolved.
            const item = {
                run: start,
                at: this._now(),
                priority,
                drop: () => resolve({
                    retryable: true, transport: false, entry: null, reason: 'queue_dropped',
                } as any),
            };
            if (priority) this._queue.unshift(item); else this._queue.push(item);
            this._drain();
        });
    }

    // Discard queued, non-priority lookups whose resolve has already given up.
    private _pruneQueue(now: number): void {
        if (this._queue.length === 0) return;
        const keep: typeof this._queue = [];
        for (const item of this._queue) {
            if (!item.priority && now - item.at > SessionSource.QUEUE_MAX_AGE_MS) {
                this._droppedFromQueue += 1;
                (item as any).drop();
                continue;
            }
            keep.push(item);
        }
        if (keep.length !== this._queue.length) {
            this._queue = keep;
            if (!this._dropLogged) {
                this._dropLogged = true;
                this._log(
                    `SessionSource: dropped ${this._droppedFromQueue} queued lookup(s) older than ` +
                    `${SessionSource.QUEUE_MAX_AGE_MS}ms — their resolve had already fallen back. ` +
                    `At ${SessionSource.MAX_PER_SECOND}/s this is what a long watchlist looks like; ` +
                    `a bulk tokens[] endpoint would remove the pacing entirely.`
                );
            }
        }
    }

    private _drain(): void {
        this._pruneQueue(this._now());
        while (this._active < SessionSource.MAX_CONCURRENT && this._queue.length > 0) {
            const now = this._now();
            this._pruneQueue(now);
            if (this._queue.length === 0) return;
            let waitMs = this._pausedUntil - now;
            // Rolling one-second window: drop starts that have aged out, and if the
            // budget for this second is spent, wait until the oldest one does.
            while (this._recentStarts.length > 0 && this._recentStarts[0] <= now - 1000) {
                this._recentStarts.shift();
            }
            if (this._recentStarts.length >= SessionSource.MAX_PER_SECOND) {
                waitMs = Math.max(waitMs, this._recentStarts[0] + 1000 - now);
            }
            if (waitMs > 0) {
                // Re-check when the wait lifts. One timer, however deep the queue.
                if (this._drainTimer === null) {
                    this._drainTimer = this._setTimer(() => { this._drainTimer = null; this._drain(); }, waitMs);
                }
                return;
            }
            this._recentStarts.push(now);
            const next = this._queue.shift();
            if (next) next.run();
        }
    }

    private _noteRateLimited(): void {
        this._pausedUntil = this._now() + SessionSource.RATE_LIMIT_PAUSE_MS;
        if (!this._rateLimitLogged) {
            this._rateLimitLogged = true;
            this._log(
                `SessionSource: data/session_history rate-limited us (429). Holding the queue for ` +
                `${SessionSource.RATE_LIMIT_PAUSE_MS}ms. We pace ourselves at ${SessionSource.MAX_PER_SECOND}/s ` +
                `against a documented 5/s per-user limit, so seeing this usually means another tab or device on ` +
                `the same login is spending from the same budget.`
            );
        }
    }

    private _sleep(ms: number): Promise<void> {
        return new Promise<void>((resolve) => { this._setTimer(resolve, ms); });
    }

    // One HTTP attempt, queued behind at most MAX_CONCURRENT others. NEVER rejects.
    private _attempt(segment: number, token: string | number, priority: boolean): Promise<Attempt> {
        return this._runQueued(() => this._attemptNow(segment, token), priority);
    }

    // The request itself. The abort timer starts HERE, not at enqueue time, so
    // sitting in the queue cannot consume a symbol's request budget.
    private _attemptNow(segment: number, token: string | number): Promise<Attempt> {
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
        const timer = ctrl
            ? this._setTimer(() => ctrl.abort(), SessionSource.FETCH_TIMEOUT_MS)
            : undefined;
        return this._post({ token: Number(token), market_segment_id: segment }, ctrl ? ctrl.signal : undefined)
            .then((meta: SessionHttpMeta): Attempt => {
                // Classify on STATUS FIRST. A 429/5xx still carries a JSON body,
                // just without `data.session` — reading that as "this symbol has no
                // override" is exactly how a transient blip becomes a permanently
                // wrong session for the rest of the page.
                if (!meta || meta.status === 0) {
                    return {
                        retryable: true, transport: true, entry: null,
                        reason: meta && meta.aborted ? 'timeout' : 'network',
                        error: meta && meta.error,
                    };
                }
                if (meta.status === 429) {
                    this._noteRateLimited();
                    return { retryable: true, transport: false, entry: null, reason: 'http_429' };
                }
                if (meta.status >= 500) {
                    return { retryable: true, transport: false, entry: null, reason: `http_${meta.status}` };
                }
                if (!meta.ok) {
                    // 4xx other than 429 is deterministic (bad token, bad auth):
                    // retrying cannot change it, so treat it as an answer.
                    return { retryable: false, transport: false, entry: null, reason: `http_${meta.status}` };
                }
                const data = meta.body && meta.body.data;
                const session = data && data.session;
                // No session field = the backend authoritatively has no override for
                // this scrip. That is the majority reply and MUST be cached, or every
                // unaffected symbol re-asks on every resolve.
                if (!session) return { retryable: false, transport: false, entry: null, reason: 'no_session' };
                const v = validateSessionString(session) as SessionValidation;
                if (!v.ok) {
                    return { retryable: false, transport: false, entry: null, reason: 'invalid', code: v.code, session };
                }
                const corrections = String((data && data.corrections) || '');
                return { retryable: false, transport: false, entry: { session: String(session), corrections }, reason: 'ok' };
            })
            .catch((e: any): Attempt => ({
                retryable: true, transport: true, entry: null,
                reason: e && e.name === 'AbortError' ? 'timeout' : 'network',
                error: (e && e.message) || String(e),
            }))
            .then((res: Attempt) => { if (timer !== undefined) this._clearTimer(timer); return res; });
    }

    // A transport failure means the request never produced an HTTP response. On
    // an environment where the route is not deployed, the browser's CORS preflight
    // fails and EVERY call lands here — so counting these is what turns an endless
    // stream of red console entries into two requests and then silence.
    private _noteTransportFailure(reason: string, error?: string): void {
        this._transportFailures += 1;
        if (this._transportFailures < SessionSource.BREAKER_TRIP_AFTER) return;
        this._breakerUntil = this._now() + SessionSource.BREAKER_MS;
        if (!this._breakerLogged) {
            this._breakerLogged = true;
            this._log(
                `SessionSource: data/session_history unreachable (${reason}${error ? ': ' + error : ''}). ` +
                `Pausing lookups for ${Math.round(SessionSource.BREAKER_MS / 1000)}s; symbols keep their aux/symbol session. ` +
                `A blocked CORS preflight looks exactly like this — check that the route is deployed on this gateway.`
            );
        }
    }

    // Any HTTP response at all — even a 500 — proves the endpoint is reachable, so
    // the transport-failure evidence is stale. Reset it and close the breaker.
    private _noteTransportSuccess(): void {
        this._transportFailures = 0;
        this._breakerUntil = 0;
    }
}

